import { Component, OnInit, OnDestroy, ViewChild, ChangeDetectorRef, HostListener, ElementRef, TemplateRef } from '@angular/core';
import { Subject } from 'rxjs';
import { CdkVirtualScrollViewport } from '@angular/cdk/scrolling';
import { ToastrService } from 'ngx-toastr';
import { MatDialog } from '@angular/material/dialog';
import { WaterAdminViewService, WaterAdminRow, WaterAdminDetailRequest, DeleteWaterAdminRequest } from '../../services/water-admin-view.service';
import { TableColumn } from '../../services/interfaces/water-admin-view.interfaces';
import { AuthService } from '../../services/auth.service';
import { ErrorHandler } from '../../utils/error-handler';
import { exportRowsToExcel } from '../../utils/excel-export';

@Component({
  selector: 'app-water-admin-view',
  standalone: false,
  templateUrl: './water-admin-view.component.html',
  styleUrls: ['./water-admin-view.component.scss']
})
export class WaterAdminViewComponent implements OnInit, OnDestroy {
  @ViewChild(CdkVirtualScrollViewport) viewport!: CdkVirtualScrollViewport;
  @ViewChild('floatingFilterPanel') floatingFilterPanel!: ElementRef;
  @ViewChild('deleteConfirmDialog') deleteConfirmDialog!: TemplateRef<any>;
  @ViewChild('exportOptionsDialog') exportOptionsDialog!: TemplateRef<any>;

  spinnerBoolean: boolean = false;
  data: WaterAdminRow[] = [];
  selectedRows: WaterAdminRow[] = [];
  showCancel: boolean = false;
  dropdownLoading: boolean = false;
  loading: boolean = false;
  hoveredRowIndex: number | null = null;
  hoverTooltip: { value: string; top: number; left: number } | null = null;
  showAddNewModal: boolean = false;
  addNewModalValue: string = '';
  addNewModalFieldLabel: string = '';
  private addNewModalRow: WaterAdminRow | null = null;
  addNewModalField: keyof WaterAdminRow | null = null;
  nextRowNumber: number | null = 0;
  hasMore: boolean = true;
  regionOptions: string[] = [];
  developmentAreaOptions: string[] = [];
  subDevelopmentAreaOptions: string[] = [];
  elementTypeOptions: string[] = [];
  pipelineOutletDevAreaOptions: string[] = [];
  feDevAreaOptions: string[] = [];
  intOutletPipelineNameOptions: string[] = [];
  enersightAssetOptions: string[] = [];
  disposalOutletTypeOptions: string[] = [];
  companyOptions: string[] = [];
  priorityOptions: number[] = [1, 2, 3, 4, 5,6,7,8,9];
  private readonly defaultPriorityOptions: number[] = [1, 2, 3, 4, 5,6,7,8,9];

  newRowDropdownOptions: {
    regions: string[];
    developmentAreas: string[];
    subDevelopmentAreas: string[];
    elementTypes: string[];
    pipelineOutletDevAreas: string[];
    feDevAreas: string[];
    intOutletPipelineNames: string[];
    enersightAssets: string[];
    disposalOutletTypes: string[];
    companies: string[];
    priorities: number[];
  } = {
    regions: [],
    developmentAreas: [],
    subDevelopmentAreas: [],
    elementTypes: [],
    pipelineOutletDevAreas: [],
    feDevAreas: [],
    intOutletPipelineNames: [],
    enersightAssets: [],
    disposalOutletTypes: [],
    companies: [],
    priorities: [1, 2, 3, 4, 5]
  };

  // Filter-related properties
  filterColumnsData: { [key: string]: string[] } = {};
  isLoadingFilterOptions: boolean = false;
  filterSearch: { [key: string]: string } = {};
  activeFilter: string = '';
  showFloatingFilter: boolean = false;
  dropdownPosition = { top: 0, left: 0 };
  multiFilters: { [key: string]: string[] } = {};
  currentFilters: { [key: string]: string | null } = {
    region: null,
    feDevArea: null,
    intOutletPipelineName: null,
    elementType: null,
    pipelineOutletDevArea: null,
    enersightAsset: null,
    disposalOutletType: null,
    company: null,
    priority: null,
  };
  dateRangeFilters: {startDate: string | null;endDate: string | null} = {startDate: null,endDate: null,};
  public dialogMessage: string = ''; private destroy$ = new Subject<void>();

  columns: TableColumn[] = [
    { field: 'region', header: 'Region', required: true, filterable: true, filterType: 'multi-select' },
    { field: 'feDevArea', header: 'Dev Area', required: true, filterable: true, filterType: 'multi-select', allowMultiple: true },
    { field: 'intOutletPipelineName', header: 'WaterFacilityName', required: true, filterable: true, filterType: 'multi-select', allowCreateNew: true },
    { field: 'elementType', header: 'Facility Type', required: true, filterable: true, filterType: 'multi-select' },
    { field: 'pipelineOutletDevArea', header: 'Pipeline Outlet Dev Area', required: false, filterable: true, filterType: 'multi-select' },
    { field: 'enersightAsset', header: 'Enersight Asset', required: false, filterable: true, filterType: 'multi-select', allowCreateNew: true },
    { field: 'disposalOutletType', header: 'Disposal Outlet Type', required: true, filterable: true, filterType: 'multi-select' },
    { field: 'company', header: 'Water Take Away Provider', required: true, filterable: true, filterType: 'multi-select' },
    { field: 'effectiveDate', header: 'Effective Date', required: false, filterable: true, filterType: 'date-range' },
    { field: 'priority', header: 'Priority', required: true, filterable: true, filterType: 'multi-select' }
  ];

  addNewRegionName: string = '';
  addNewDevelopmentArea: string = '';

  constructor(
    private toastr: ToastrService,
    private waterAdminService: WaterAdminViewService,
    private authService: AuthService,
    private cdr: ChangeDetectorRef,
    private elementRef: ElementRef,
    private dialog: MatDialog
  ) {}

  async ngOnInit(): Promise<void> {
    await this.loadInitialDropdowns();
    await this.loadWaterAdminData();
    this.initializeFilterOptions();
  }

  ngOnDestroy(): void {
    this.destroy$.next();
    this.destroy$.complete();
  }

  async loadInitialDropdowns(): Promise<void> {
    try {
      this.dropdownLoading = true;
      const dropdownData = await this.waterAdminService.getAllDropdownOptions();
      this.applyDropdownOptions(dropdownData);

    } catch (error: any) {
      const errorMessage = error.message || 'Failed to load dropdown options';
      this.toastr.error(errorMessage, 'Error');
    } finally {
      this.dropdownLoading = false;
    }
  }

  initializeFilterOptions(): void {
    this.filterColumnsData['region'] = [...this.regionOptions];
    this.filterColumnsData['elementType'] = [...this.elementTypeOptions];
    this.filterColumnsData['pipelineOutletDevArea'] = [...this.pipelineOutletDevAreaOptions];
    this.filterColumnsData['feDevArea'] = [...this.feDevAreaOptions];
    this.filterColumnsData['intOutletPipelineName'] = [...this.intOutletPipelineNameOptions];
    this.filterColumnsData['enersightAsset'] = [...this.enersightAssetOptions];
    this.filterColumnsData['disposalOutletType'] = [...this.disposalOutletTypeOptions];
    this.filterColumnsData['company'] = [...this.companyOptions];
    this.filterColumnsData['priority'] = this.priorityOptions.map(p => p.toString());
  }

  private updateFilterColumnsFromDropdownData(dropdownData: {
    regions: string[];
    elementTypes: string[];
    pipelineOutletDevAreas: string[];
    developmentAreas?: string[];
    subDevelopmentAreas?: string[];
    feDevAreas: string[];
    intOutletPipelineNames: string[];
    enersightAssets: string[];
    disposalOutletTypes: string[];
    companies: string[];
    priorities: number[];
  }): void {
    this.filterColumnsData['region'] = [...(dropdownData.regions || [])];
    this.filterColumnsData['elementType'] = [...(dropdownData.elementTypes || [])];
    this.filterColumnsData['pipelineOutletDevArea'] = [...(dropdownData.pipelineOutletDevAreas || [])];
    this.filterColumnsData['feDevArea'] = [...(dropdownData.feDevAreas || [])];
    this.filterColumnsData['intOutletPipelineName'] = [...(dropdownData.intOutletPipelineNames || [])];
    this.filterColumnsData['enersightAsset'] = [...(dropdownData.enersightAssets || [])];
    this.filterColumnsData['disposalOutletType'] = [...(dropdownData.disposalOutletTypes || [])];
    this.filterColumnsData['company'] = [...(dropdownData.companies || [])];
    this.filterColumnsData['priority'] = (dropdownData.priorities || []).map(p => p.toString());
  }

  private applyDropdownOptions(dropdownData: {
    regions: string[];
    developmentAreas?: string[];
    subDevelopmentAreas?: string[];
    elementTypes: string[];
    pipelineOutletDevAreas: string[];
    feDevAreas: string[];
    intOutletPipelineNames: string[];
    enersightAssets: string[];
    disposalOutletTypes: string[];
    companies: string[];
    priorities: number[];
  }): void {
    const uniqueValues = (values: string[]): string[] => [...new Set((values || []).filter(Boolean))];
    const normalizePriorities = (values?: number[]): number[] => {
      const parsed = (values || []).filter((value): value is number => Number.isFinite(value));
      return parsed.length > 0 ? [...new Set(parsed)].sort((a, b) => a - b) : [...this.defaultPriorityOptions];
    };
    const developmentAreas = uniqueValues(dropdownData.developmentAreas || []);
    const subDevelopmentAreas = uniqueValues(dropdownData.subDevelopmentAreas || []);
    const combinedFeDevAreas = uniqueValues([
      ...(dropdownData.feDevAreas || []),
      ...developmentAreas,
      ...subDevelopmentAreas
    ]);

    this.regionOptions = dropdownData.regions || [];
    this.developmentAreaOptions = developmentAreas;
    this.subDevelopmentAreaOptions = subDevelopmentAreas;
    this.elementTypeOptions = dropdownData.elementTypes || [];
    this.pipelineOutletDevAreaOptions = dropdownData.pipelineOutletDevAreas || [];
    this.feDevAreaOptions = combinedFeDevAreas;
    this.intOutletPipelineNameOptions = dropdownData.intOutletPipelineNames || [];
    this.enersightAssetOptions = dropdownData.enersightAssets || [];
    this.disposalOutletTypeOptions = dropdownData.disposalOutletTypes || [];
    this.companyOptions = dropdownData.companies || [];
    this.priorityOptions = normalizePriorities(dropdownData.priorities);
  }

  private resetNewRowDropdownOptions(): void {
    this.newRowDropdownOptions = {
      regions: [...this.regionOptions],
      developmentAreas: [...this.developmentAreaOptions],
      subDevelopmentAreas: [...this.subDevelopmentAreaOptions],
      elementTypes: [...this.elementTypeOptions],
      pipelineOutletDevAreas: [...this.pipelineOutletDevAreaOptions],
      feDevAreas: [...this.feDevAreaOptions],
      intOutletPipelineNames: [...this.intOutletPipelineNameOptions],
      enersightAssets: [...this.enersightAssetOptions],
      disposalOutletTypes: [...this.disposalOutletTypeOptions],
      companies: [...this.companyOptions],
      priorities: [...this.priorityOptions]
    };
  }

  private withCurrentValue(options: string[], value: string): string[] {
    if (!value) {
      return options;
    }
    return options.includes(value) ? options : [value, ...options];
  }

  private withCurrentPriority(options: number[], value: number | null): number[] {
    if (value === null) {
      return options;
    }
    return options.includes(value) ? options : [value, ...options];
  }

  getFeDevAreaOptions(row: WaterAdminRow): string[] {
    const options = row.isNew ? this.newRowDropdownOptions.feDevAreas : this.feDevAreaOptions;
    return this.withCurrentValue(options, row.feDevArea);
  }

  getDevelopmentAreaOptions(row: WaterAdminRow): string[] {
    const options = row.isNew
      ? this.newRowDropdownOptions.developmentAreas
      : this.developmentAreaOptions;
    return this.withCurrentValue(options, row.feDevArea);
  }

  getSubDevelopmentAreaOptions(row: WaterAdminRow): string[] {
    const options = row.isNew
      ? this.newRowDropdownOptions.subDevelopmentAreas
      : this.subDevelopmentAreaOptions;
    return this.withCurrentValue(options, row.feDevArea);
  }

  getRegionOptions(row: WaterAdminRow): string[] {
    const options = row.isNew ? this.newRowDropdownOptions.regions : this.regionOptions;
    return this.withCurrentValue(options, row.region);
  }

  getFacilityNameOptions(row: WaterAdminRow): string[] {
    const options = row.isNew ? this.newRowDropdownOptions.intOutletPipelineNames : this.intOutletPipelineNameOptions;
    return this.withCurrentValue(options, row.intOutletPipelineName);
  }

  getElementTypeOptions(row: WaterAdminRow): string[] {
    const options = row.isNew ? this.newRowDropdownOptions.elementTypes : this.elementTypeOptions;
    return this.withCurrentValue(options, row.elementType);
  }

  getPipelineOutletDevAreaOptions(row: WaterAdminRow): string[] {
    const options = row.isNew ? this.newRowDropdownOptions.pipelineOutletDevAreas : this.pipelineOutletDevAreaOptions;
    return this.withCurrentValue(options, row.pipelineOutletDevArea);
  }

  getEnersightAssetOptions(row: WaterAdminRow): string[] {
    const options = row.isNew ? this.newRowDropdownOptions.enersightAssets : this.enersightAssetOptions;
    return this.withCurrentValue(options, row.enersightAsset);
  }

  getDisposalOutletTypeOptions(row: WaterAdminRow): string[] {
    const options = row.isNew ? this.newRowDropdownOptions.disposalOutletTypes : this.disposalOutletTypeOptions;
    return this.withCurrentValue(options, row.disposalOutletType);
  }

  getCompanyOptions(row: WaterAdminRow): string[] {
    const options = row.isNew ? this.newRowDropdownOptions.companies : this.companyOptions;
    return this.withCurrentValue(options, row.company);
  }

  getPriorityOptions(row: WaterAdminRow): number[] {
    const options = row.isNew ? this.newRowDropdownOptions.priorities : this.priorityOptions;
    return this.withCurrentPriority(options, row.priority);
  }

  private async refreshCascadingDropdowns(row: WaterAdminRow): Promise<void> {
    if (!row.isNew) {
      return;
    }

    try {
      this.dropdownLoading = true;
      const response = await this.waterAdminService.getCascadingFilterOptions({
        waterFacilityName: row.intOutletPipelineName || null,
        region: row.region || null,
        feDevArea: row.feDevArea || null,
        pipelineOutletDevArea: row.pipelineOutletDevArea || null,
        company: row.company || null,
        enersightAsset: row.enersightAsset || null,
        disposalOutletType: row.disposalOutletType || null,
        facilityType: row.elementType || null,
        priority: row.priority ?? null
      });

      const dropdownData = {
        regions: response.find(item => item.columnName?.toLowerCase() === 'region')?.values || [],
        developmentAreas: response.find(item => item.columnName?.toLowerCase() === 'developmentarea')?.values || [],
        subDevelopmentAreas: response.find(item => item.columnName?.toLowerCase() === 'subdevelopmentarea')?.values || [],
        elementTypes: response.find(item => item.columnName?.toLowerCase() === 'facilitytype')?.values || [],
        pipelineOutletDevAreas: response.find(item => item.columnName?.toLowerCase() === 'pipelineoutletdevarea')?.values || [],
        feDevAreas:
          response.find(item => item.columnName?.toLowerCase() === 'fedevarea')?.values ||
          response.find(item => item.columnName?.toLowerCase() === 'subdevelopmentarea')?.values ||
          [],
        intOutletPipelineNames:
          response.find(item => item.columnName?.toLowerCase() === 'waterfacilityname')?.values ||
          response.find(item => item.columnName?.toLowerCase() === 'facilityname')?.values ||
          [],
        enersightAssets: response.find(item => item.columnName?.toLowerCase() === 'enersightasset')?.values || [],
        disposalOutletTypes: response.find(item => item.columnName?.toLowerCase() === 'disposaloutlettype')?.values || [],
        companies: response.find(item => item.columnName?.toLowerCase() === 'company')?.values || [],
        priorities: (() => {
          const values = (response.find(item => item.columnName?.toLowerCase() === 'priority')?.values || [])
            .map(v => Number(v))
            .filter(v => Number.isFinite(v));
          return values.length > 0 ? values : [...this.defaultPriorityOptions];
        })()
      };

      this.newRowDropdownOptions = {
        regions: dropdownData.regions,
        developmentAreas: dropdownData.developmentAreas,
        subDevelopmentAreas: dropdownData.subDevelopmentAreas,
        elementTypes: dropdownData.elementTypes,
        pipelineOutletDevAreas: dropdownData.pipelineOutletDevAreas,
        feDevAreas: dropdownData.feDevAreas,
        intOutletPipelineNames: dropdownData.intOutletPipelineNames,
        enersightAssets: dropdownData.enersightAssets,
        disposalOutletTypes: dropdownData.disposalOutletTypes,
        companies: dropdownData.companies,
        priorities: dropdownData.priorities
      };
    } catch (error: any) {
      const errorMessage = error.message || 'Failed to load cascading dropdown options';
      this.toastr.error(errorMessage, 'Error');
    } finally {
      this.dropdownLoading = false;
    }
  }

  private syncOptionsFromRows(rows: WaterAdminRow[]): void {
    const uniqueStrings = (values: string[]) =>
      [...new Set(values.filter(v => v !== null && v !== undefined && v !== '').map(v => v.trim()))];

    this.feDevAreaOptions = uniqueStrings([...this.feDevAreaOptions, ...rows.map(r => r.feDevArea)]);
    this.regionOptions = uniqueStrings([...this.regionOptions, ...rows.map(r => r.region)]);
    this.intOutletPipelineNameOptions = uniqueStrings([
      ...this.intOutletPipelineNameOptions,
      ...rows.map(r => r.intOutletPipelineName)
    ]);
    this.elementTypeOptions = uniqueStrings([...this.elementTypeOptions, ...rows.map(r => r.elementType)]);
    this.pipelineOutletDevAreaOptions = uniqueStrings([
      ...this.pipelineOutletDevAreaOptions,
      ...rows.map(r => r.pipelineOutletDevArea)
    ]);
    this.enersightAssetOptions = uniqueStrings([...this.enersightAssetOptions, ...rows.map(r => r.enersightAsset)]);
    this.disposalOutletTypeOptions = uniqueStrings([
      ...this.disposalOutletTypeOptions,
      ...rows.map(r => r.disposalOutletType)
    ]);
    this.companyOptions = uniqueStrings([...this.companyOptions, ...rows.map(r => r.company)]);

    const mergedPriority = [...this.priorityOptions, ...rows.map(r => r.priority).filter((p): p is number => p !== null)];
    this.priorityOptions = [...new Set(mergedPriority)].sort((a, b) => a - b);
  }

  async loadWaterAdminData(filters?: Partial<WaterAdminDetailRequest>): Promise<void> {
    try {
      this.spinnerBoolean = true;
      
      const filterPayload: WaterAdminDetailRequest = {
        region: this.currentFilters['region'] || null,
        feDevArea: this.currentFilters['feDevArea'] || null,
        intOutletPipelineName: this.currentFilters['intOutletPipelineName'] || null,
        elementType: this.currentFilters['elementType'] || null,
        pipelineOutletDevArea: this.currentFilters['pipelineOutletDevArea'] || null,
        enersightAsset: this.currentFilters['enersightAsset'] || null,
        disposalOutletType: this.currentFilters['disposalOutletType'] || null,
        company: this.currentFilters['company'] || null,
        startDate: this.dateRangeFilters.startDate || null,
        endDate: this.dateRangeFilters.endDate || null,
        priority: this.currentFilters['priority'] ? Number(this.currentFilters['priority']) : null,
        lastRowNumber: filters?.lastRowNumber ?? 0,
        ...filters
      };

      const response = await this.waterAdminService.getWaterAdminDataWithPagination(filterPayload);
      
      this.nextRowNumber = response.nextRowNumber ?? null;
      this.hasMore = this.nextRowNumber !== null && this.nextRowNumber > 0;

      if (response.data && response.data.length > 0) {
        let newRows = response.data.map((item): WaterAdminRow => ({
          _rowId: this.generateRowId(),
          region: item.region,
          feDevArea: item.feDevArea,
          intOutletPipelineName: item.intOutletPipelineName,
          elementType: item.elementType,
          facilityType: item.facilityType, // Added missing property
          pipelineOutletDevArea: item.pipelineOutletDevArea,
          enersightAsset: item.enersightAsset,
          disposalOutletType: item.disposalOutletType,
          company: item.company,
          effectiveDate: item.effectiveDate || '',
          priority: item.priority,
          isNew: false,
          _rowEdited: false,
          original: {}
        }));

        for (const row of newRows) {
          row.original = { ...row };
        }

        this.syncOptionsFromRows(newRows);
        this.initializeFilterOptions();

        if (filterPayload.lastRowNumber === 0) {
          this.data = newRows;
        } else {
          this.mergeRows(newRows);
        }

      } else {
        if (filterPayload.lastRowNumber === 0) {
          this.data = [];
          if (!this.hasAnyFilterApplied()) {
            this.toastr.info('No existing water admin data found', 'Info');
          }
        }
      }
    } catch (error: any) {
      const errorMessage = error.message || 'Failed to load water admin data';
      this.toastr.error(errorMessage, 'Error');
      this.hasMore = false;
    } finally {
      this.spinnerBoolean = false;
      this.loading = false;
    }
  }

  private mergeRows(newRows: WaterAdminRow[]): void {
    const keyOf = (r: WaterAdminRow) =>
      `${r.region}||${r.feDevArea}||${r.intOutletPipelineName}||${r.elementType}||${r.effectiveDate}`;

    const existingKeys = new Set(this.data.map(keyOf));
    const toAdd = newRows.filter((r) => !existingKeys.has(keyOf(r)));
    this.data = [...this.data, ...toAdd];
  }

  loadFirstPage(): void {
    this.data = [];
    this.nextRowNumber = 0;
    this.hasMore = true;
    this.loadWaterAdminData({ lastRowNumber: 0 });
  }

  onScrolledIndexChange(index: number): void {
    this.hideHoverTooltip();
    const buffer = 10;
    const renderedEnd = this.viewport?.getRenderedRange().end ?? index;
    if (
      this.hasMore &&
      !this.loading &&
      !this.spinnerBoolean &&
      renderedEnd >= Math.max(0, this.data.length - buffer)
    ) {
      this.loading = true;
      this.loadWaterAdminData({ lastRowNumber: this.nextRowNumber ?? 0 });
    }
  }

  addRow(): void {
    this.resetNewRowDropdownOptions();
    const newRow: WaterAdminRow = { 
      _rowId: this.generateRowId(), 
      region: '',
      feDevArea: '', 
      intOutletPipelineName: '', 
      elementType: '', 
      facilityType: '', // Added missing property
      pipelineOutletDevArea: '', 
      enersightAsset: '', 
      disposalOutletType: '', 
      company: '', 
      effectiveDate: '', 
      priority: null,
      isNew: true, 
      _rowEdited: false, 
      original: {} 
    };
    
    this.data = [newRow, ...this.data];
    this.showCancel = true;
    this.clearSelection();
    
    this.cdr.detectChanges();
    setTimeout(() => {
      this.viewport?.checkViewportSize();
      this.viewport?.scrollToIndex(0, 'smooth');
    });
    
    this.toastr.info('New water admin row added. Click Cancel to discard or Save to keep changes.');
  }

  copyRow(index: number): void {
    const originalRow = this.data[index];
    if (!originalRow) return;

    this.resetNewRowDropdownOptions();

    const copiedRow: WaterAdminRow = {
      _rowId: this.generateRowId(),
      region: originalRow.region,
      feDevArea: originalRow.feDevArea,
      intOutletPipelineName: originalRow.intOutletPipelineName,
      elementType: originalRow.elementType,
      facilityType: originalRow.facilityType, // Added missing property
      pipelineOutletDevArea: originalRow.pipelineOutletDevArea,
      enersightAsset: originalRow.enersightAsset,
      disposalOutletType: originalRow.disposalOutletType,
      company: originalRow.company,
      effectiveDate: '',
      priority: originalRow.priority,
      isNew: true,
      _rowEdited: false,
      original: {}
    };

    this.data = [copiedRow, ...this.data];
    this.showCancel = true;
    this.clearSelection();

    this.cdr.detectChanges();
    setTimeout(() => {
      this.viewport?.checkViewportSize();
      this.viewport?.scrollToIndex(0, 'smooth');
    }, 100);

    this.toastr.info('Row copied successfully. Click Cancel to discard or Save to keep changes.');
  }

  async handleSave(): Promise<void> {
    const userName = this.authService.getUserName();
    let successCount = 0;
    let failureCount = 0;
    let errorMessages: string[] = [];

    try {
      const required = this.columns.filter(col => col.required).map(col => col.field);
      const changedRows = this.data.filter(row => row.isNew || row._rowEdited);
      
      if (changedRows.length === 0) {
        this.toastr.info('No changes to save.');
        return;
      }

      const invalid = changedRows.filter(row => 
        required.some(field => {
          const value = row[field];
          return value === undefined || value === null || value === '';
        })
      );
      
      if (invalid.length) { 
        this.toastr.error('Please fill all required fields before saving.'); 
        return; 
      }

      this.spinnerBoolean = true;
      
      for (const row of changedRows) {
        const action: 'INSERT' | 'UPDATE' = row.isNew ? 'INSERT' : 'UPDATE';
        const originalRow = row.original as WaterAdminRow;
        
        const response = await this.waterAdminService.saveWaterAdmin(row, action, userName, originalRow);
        const parsed = ErrorHandler.parseApiResult(response);

        if (parsed.success) {
          row.isNew = false;
          row._rowEdited = false;
          row.original = { ...row };
          successCount++;
        } else {
          failureCount++;
          errorMessages.push(parsed.message || 'Unknown error occurred');
        }
      }


      if (successCount > 0) {
        this.toastr.success(`${successCount} water admin row(s) saved successfully`, 'Success');
        this.showCancel = false;
      }

      if (failureCount > 0) {
        const detailedErrorMsg = errorMessages.length > 0
          ? errorMessages.join('<br>')
          : `${failureCount} water admin row(s) failed to save`;
        
        this.toastr.error(detailedErrorMsg, 'Save Error', {
          timeOut: 8000,
          enableHtml: true
        });
        this.loadWaterAdminData();
      }
    } catch (error: any) {
      const parsed = ErrorHandler.parseApiResult(error?.error || error);
      this.toastr.error(parsed.message || 'Failed to save water admin data', 'Error');
    } 
    finally {
      this.spinnerBoolean = false;
    }
  }

  async handleDelete(): Promise<void> {
    if (!this.selectedRows.length) { 
      this.toastr.info('Please select rows to delete.'); 
      return; 
    }

    this.dialogMessage = `Are you sure you want to delete ${this.selectedRows.length} row(s)? This action cannot be undone.`;

    const dialogRef = this.dialog.open(this.deleteConfirmDialog, {
      width: '400px',
      disableClose: true,
    });

    const confirmed = await dialogRef.afterClosed().toPromise();
    if (!confirmed) return;

    const userName = this.authService.getUserName();

    let successCount = 0;
    let failureCount = 0;
    let errorMessages: string[] = [];

    try {
      this.spinnerBoolean = true;

      for (const row of this.selectedRows) {
        const requestBody: DeleteWaterAdminRequest = {
          waterFacilityName: row.intOutletPipelineName,
          facilityType: row.elementType,
          feDevArea: row.feDevArea,
          pipelineDevArea: row.pipelineOutletDevArea,
          company: row.company,
          enersightAsset: row.enersightAsset,
          region: row.region,
          disposalOutletType: row.disposalOutletType,
          userName: userName
        };

        const response = await this.waterAdminService.deleteWaterAdmin(row, userName);
        const parsed = ErrorHandler.parseApiResult(response);

        if (parsed.success) {
          successCount++;
        } else {
          failureCount++;
          errorMessages.push(parsed.message || 'Unknown error occurred');
        }
      }

      this.data = this.data.filter(row => !this.selectedRows.includes(row));
      this.selectedRows = [];

      await this.loadWaterAdminData();
      
      if (successCount > 0) {
        this.toastr.success(`${successCount} row(s) deleted successfully.`);
      }
      if (failureCount > 0) {
        const detailedErrorMsg = errorMessages.length > 0
          ? errorMessages.join('<br>')
          : `${failureCount} row(s) failed to delete`;
        
        this.toastr.error(detailedErrorMsg, 'Delete Error', {
          timeOut: 8000,
          enableHtml: true
        });
      }
    } catch (error: any) {
      const parsed = ErrorHandler.parseApiResult(error?.error || error);
      this.toastr.error(parsed.message || 'Failed to delete water admin data', 'Error');
    } finally {
      this.spinnerBoolean = false;
    }
  }

  cancelRow(): void {
    this.data = this.data.filter((row) => !row.isNew);
    
    this.data.forEach(row => {
      if (row._rowEdited && row.original && Object.keys(row.original).length > 0) {
        Object.assign(row, row.original);
        row._rowEdited = false;
      }
    });
    
    this.showCancel = false;
    this.clearSelection();
    
    this.cdr.detectChanges();
    
    setTimeout(() => {
      this.viewport?.checkViewportSize();
    });
    
    this.toastr.info('Changes cancelled.');
  }

  clearSelection(): void {
    this.selectedRows = [];
  }

  toggleRowSelection(row: WaterAdminRow, ev: Event): void {
    const target = ev.target as HTMLInputElement;
    if (target.checked) { 
      if (!this.selectedRows.includes(row)) this.selectedRows.push(row); 
    } else { 
      const index = this.selectedRows.indexOf(row); 
      if (index > -1) this.selectedRows.splice(index, 1); 
    }
  }

  markAsEdited(row: WaterAdminRow): void {
    if (!row.original || Object.keys(row.original).length === 0) {
      row.original = { ...row };
    }
    if (!row.isNew) {
      row._rowEdited = true;
      this.showCancel = true;
    }
  }

  handleEdit(row: WaterAdminRow): void { 
    this.markAsEdited(row);
  }

  isPipelineOutletDevAreaDisabled(row: WaterAdminRow): boolean {
    const facilityType = (row.elementType || '').trim().toLowerCase();
    if (!facilityType) {
      return false;
    }

    return facilityType === 'int' || facilityType === 'outlet';
  }

  async onDropdownSelectionChange(row: WaterAdminRow): Promise<void> {
    if (this.isPipelineOutletDevAreaDisabled(row) && row.pipelineOutletDevArea) {
      row.pipelineOutletDevArea = '';
    }

    this.markAsEdited(row);
  }

  isFieldEditable(row: WaterAdminRow, field: string): boolean {
    if (row.isNew) {
      return true;
    }

    return ['intOutletPipelineName', 'enersightAsset', 'priority'].includes(field);
  }

  validatePriority(row: WaterAdminRow, ev: Event): void {
    const target = ev.target as HTMLInputElement;
    const value = parseInt(target.value);
    row.priority = isNaN(value) ? null : value;
    this.markAsEdited(row);
  }

  canAddNewDropdownValue(row: WaterAdminRow, field: string): boolean {
    if (!row.isNew) {
      return false;
    }

    return ['intOutletPipelineName', 'region', 'company', 'enersightAsset'].includes(field);
  }

  async onDropdownFieldChange<K extends keyof WaterAdminRow>(
    row: WaterAdminRow,
    field: K,
    value: WaterAdminRow[K] | null
  ): Promise<void> {
    if (value === '__ADD_NEW__') {
      await this.handleAddNewDropdownValue(row, field);
      return;
    }

    if (field in row && value !== undefined && value !== null) {
      row[field] = value;
      this.markAsEdited(row);

      if (field === 'region') {
        row.feDevArea = '';
        row.pipelineOutletDevArea = '';

        if (row.isNew && typeof value === 'string' && value.trim()) {
          await this.refreshCascadingDropdowns(row);
        }
      }
    }
  }

  private getAddNewFieldDisplayName(field: keyof WaterAdminRow): string {
    const labels: Partial<Record<keyof WaterAdminRow, string>> = {
      intOutletPipelineName: 'Facility Name',
      region: 'Region',
      company: 'Company',
      enersightAsset: 'Enersight Asset'
    };

    return labels[field] || String(field);
  }

  private isSupportedAddNewField(
    field: keyof WaterAdminRow
  ): field is 'region' | 'intOutletPipelineName' | 'company' | 'enersightAsset' {
    return ['region', 'intOutletPipelineName', 'company', 'enersightAsset'].includes(field);
  }

  private addValueToDropdownOptions(
    field: 'region' | 'intOutletPipelineName' | 'company' | 'enersightAsset',
    value: string
  ): void {
    const addUniqueSorted = (options: string[]): string[] => {
      if (!options.includes(value)) {
        options.push(value);
      }
      return [...options].sort((a, b) => a.localeCompare(b));
    };

    switch (field) {
      case 'region':
        this.regionOptions = addUniqueSorted(this.regionOptions);
        this.newRowDropdownOptions.regions = addUniqueSorted(this.newRowDropdownOptions.regions);
        break;
      case 'intOutletPipelineName':
        this.intOutletPipelineNameOptions = addUniqueSorted(this.intOutletPipelineNameOptions);
        this.newRowDropdownOptions.intOutletPipelineNames = addUniqueSorted(this.newRowDropdownOptions.intOutletPipelineNames);
        break;
      case 'company':
        this.companyOptions = addUniqueSorted(this.companyOptions);
        this.newRowDropdownOptions.companies = addUniqueSorted(this.newRowDropdownOptions.companies);
        break;
      case 'enersightAsset':
        this.enersightAssetOptions = addUniqueSorted(this.enersightAssetOptions);
        this.newRowDropdownOptions.enersightAssets = addUniqueSorted(this.newRowDropdownOptions.enersightAssets);
        break;
      default:
        break;
    }

    this.initializeFilterOptions();
  }

  getAddNewPopupDevelopmentAreaOptions(): string[] {
    const row = this.addNewModalRow;
    const values = row && row.isNew
      ? this.newRowDropdownOptions.developmentAreas
      : this.developmentAreaOptions;

    return [...new Set(values.filter(Boolean).map(value => value.trim()))].sort((a, b) => a.localeCompare(b));
  }

  getAddNewPopupSubDevelopmentAreaOptions(): string[] {
    const row = this.addNewModalRow;
    const values = row && row.isNew
      ? this.newRowDropdownOptions.subDevelopmentAreas
      : this.subDevelopmentAreaOptions;

    return [...new Set(values.filter(Boolean).map(value => value.trim()))].sort((a, b) => a.localeCompare(b));
  }

  getAddNewDevelopmentAreaOptions(): string[] {
    return [
      ...this.getAddNewPopupDevelopmentAreaOptions(),
      ...this.getAddNewPopupSubDevelopmentAreaOptions()
    ];
  }

  private async handleAddNewDropdownValue(row: WaterAdminRow, field: keyof WaterAdminRow): Promise<void> {
    this.resetAddNewSentinelSelection(row, field);

    this.addNewModalRow = row;
    this.addNewModalField = field;
    this.addNewModalValue = '';

    if (field === 'region') {
      this.addNewModalFieldLabel = 'Region and Development Area';
      this.addNewRegionName = '';
      this.addNewDevelopmentArea = '';
    } else {
      this.addNewModalFieldLabel = this.getAddNewFieldDisplayName(field);
    }

    this.showAddNewModal = true;
  }

  closeAddNewModal(): void {
    if (this.addNewModalRow && this.addNewModalField) {
      this.resetAddNewSentinelSelection(this.addNewModalRow, this.addNewModalField);
    }

    this.showAddNewModal = false;
    this.addNewModalValue = '';
    this.addNewRegionName = '';
    this.addNewDevelopmentArea = '';
    this.addNewModalFieldLabel = '';
    this.addNewModalRow = null;
    this.addNewModalField = null;
  }

  private resetAddNewSentinelSelection(
    row: WaterAdminRow,
    field: keyof WaterAdminRow
  ): void {
    // Defer once so ngModel's internal update cannot re-apply '__ADD_NEW__'.
    setTimeout(() => {
      if ((row as any)[field] === '__ADD_NEW__') {
        (row as any)[field] = '';
        this.cdr.detectChanges();
      }
    });
  }

  async confirmAddNewModal(): Promise<void> {
    if (!this.addNewModalRow || !this.addNewModalField) {
      this.closeAddNewModal();
      return;
    }

    if (this.addNewModalField === 'region') {
      const regionName = this.addNewRegionName.trim();
      const developmentArea = this.addNewDevelopmentArea.trim();

      if (!regionName || !developmentArea) {
        this.toastr.warning('Please enter valid names for both Region and Development Area.');
        return;
      }

      this.addValueToDropdownOptions('region', regionName);

      this.addNewModalRow.region = regionName;
      this.addNewModalRow.feDevArea = developmentArea;
      await this.onDropdownSelectionChange(this.addNewModalRow);

      this.toastr.success('Added new Region and Development Area. They will be saved when you click Save.');
      this.closeAddNewModal();
      return;
    }

    const saved = await this.saveAddNewDropdownValue(
      this.addNewModalRow,
      this.addNewModalField,
      this.addNewModalValue
    );

    if (saved) {
      this.closeAddNewModal();
    }
  }

  private async saveAddNewDropdownValue(
    row: WaterAdminRow,
    field: keyof WaterAdminRow,
    rawValue: string
  ): Promise<boolean> {
    if (!this.isSupportedAddNewField(field)) {
      return false;
    }

    const fieldLabel = this.getAddNewFieldDisplayName(field);
    const newValue = rawValue.trim();

    if (!newValue) {
      this.toastr.warning(`Please enter a valid ${fieldLabel}.`);
      return false;
    }

    const existingOptions = this.getOptionsForField(row, field).map(v => v.trim().toLowerCase());
    if (existingOptions.includes(newValue.toLowerCase())) {
      (row as any)[field] = newValue;
      await this.onDropdownSelectionChange(row);
      this.toastr.info(`${fieldLabel} already exists and has been selected.`);
      return true;
    }

    this.addValueToDropdownOptions(field, newValue);
    (row as any)[field] = newValue;
    await this.onDropdownSelectionChange(row);
    this.toastr.success(`Added new ${fieldLabel}. It will be saved when you click Save.`);
    return true;
  }

  private getOptionsForField(row: WaterAdminRow, field: keyof WaterAdminRow): string[] {
    switch (field) {
      case 'region':
        return this.getRegionOptions(row);
      case 'intOutletPipelineName':
        return this.getFacilityNameOptions(row);
      case 'company':
        return this.getCompanyOptions(row);
      case 'enersightAsset':
        return this.getEnersightAssetOptions(row);
      default:
        return [];
    }
  }

  // Filter methods
  async openFloatingFilter(event: MouseEvent, field: string): Promise<void> {
    event.stopPropagation();

    if (this.showFloatingFilter && this.activeFilter === field) {
      this.closeFloatingFilter();
      return;
    }

    if (this.getFilterType(field) === 'multi-select') {
      try {
        await this.refreshFilterOptionsFromCurrentSelection();
      } catch (error: any) {
        this.toastr.error(error.message || 'Failed to refresh filter options', 'Error');
      }
    }

    const target = event.target as HTMLElement;
    const rect = target.getBoundingClientRect();
    this.dropdownPosition = {
      top: rect.bottom - 130 + window.scrollY,
      left: rect.left - 90 + window.scrollX,
    };
    this.activeFilter = field;
    this.showFloatingFilter = true;
  }

  closeFloatingFilter(): void {
    this.showFloatingFilter = false;
    this.activeFilter = '';
  }

  showHoverTooltip(event: MouseEvent, value: string | number | null | undefined): void {
    const tooltipValue = value === null || value === undefined ? '' : String(value).trim();

    if (!tooltipValue) {
      this.hoverTooltip = null;
      return;
    }

    this.hoverTooltip = {
      value: tooltipValue,
      top: event.clientY + 12,
      left: event.clientX + 16
    };
  }

  moveHoverTooltip(event: MouseEvent): void {
    if (!this.hoverTooltip) {
      return;
    }

    this.hoverTooltip = {
      ...this.hoverTooltip,
      top: event.clientY + 12,
      left: event.clientX + 16
    };
  }

  hideHoverTooltip(): void {
    this.hoverTooltip = null;
  }

  @HostListener('document:click', ['$event'])
  onDocumentClick(event: MouseEvent): void {
    if (this.showFloatingFilter) {
      const target = event.target as HTMLElement;
      const clickedInside = this.floatingFilterPanel?.nativeElement.contains(target);
      const clickedOnFilterIcon = target.classList.contains('filter-icon') ||
        target.parentElement?.classList.contains('filter-icon');

      if (!clickedInside && !clickedOnFilterIcon) {
        this.closeFloatingFilter();
      }
    }
  }

  @HostListener('window:scroll')
  @HostListener('window:resize')
  onViewportChanged(): void {
    this.hideHoverTooltip();
  }

  getFilterOptions(field: string): string[] {
    const source =
      this.filterColumnsData[field]?.length > 0
        ? this.filterColumnsData[field]
        : this.getFallbackOptionsForField(field);

    const search = (this.filterSearch[field] || '').toLowerCase();
    return source.filter(opt => opt && String(opt).toLowerCase().includes(search));
  }

  private getFallbackOptionsForField(field: string): string[] {
    switch (field) {
      case 'region':
        return [...this.regionOptions];
      case 'elementType':
        return [...this.elementTypeOptions];
      case 'pipelineOutletDevArea':
        return [...this.pipelineOutletDevAreaOptions];
      case 'feDevArea':
        return [...this.feDevAreaOptions];
      case 'intOutletPipelineName':
        return [...this.intOutletPipelineNameOptions];
      case 'enersightAsset':
        return [...this.enersightAssetOptions];
      case 'disposalOutletType':
        return [...this.disposalOutletTypeOptions];
      case 'company':
        return [...this.companyOptions];
      case 'priority':
        return this.priorityOptions.map(p => String(p));
      default:
        return [];
    }
  }

  toggleMultiFilter(field: string, value: string, event: any): void {
    if (!this.multiFilters[field]) this.multiFilters[field] = [];
    const isChecked = event.target.checked;
    if (isChecked) {
      if (!this.multiFilters[field].includes(value))
        this.multiFilters[field].push(value);
    } else {
      this.multiFilters[field] = this.multiFilters[field].filter(v => v !== value);
    }
  }

  isChecked(field: string, value: string): boolean {
    return this.multiFilters[field]?.includes(value) || false;
  }

  getFilterType(field: string): 'multi-select' | 'date-range' | 'search' {
    const column = this.columns.find(col => col.field === field);
    return column?.filterType || 'multi-select';
  }

  private async refreshFilterOptionsFromCurrentSelection(): Promise<void> {
    const filters: Partial<WaterAdminDetailRequest> = {
      region: this.currentFilters['region'] || null,
      feDevArea: this.currentFilters['feDevArea'] || null,
      intOutletPipelineName: this.currentFilters['intOutletPipelineName'] || null,
      elementType: this.currentFilters['elementType'] || null,
      pipelineOutletDevArea: this.currentFilters['pipelineOutletDevArea'] || null,
      enersightAsset: this.currentFilters['enersightAsset'] || null,
      disposalOutletType: this.currentFilters['disposalOutletType'] || null,
      company: this.currentFilters['company'] || null,
      priority: this.currentFilters['priority'] ? Number(this.currentFilters['priority']) : null,
    };

    const dropdownData = await this.waterAdminService.getMultiFilterOptions(filters);
    this.updateFilterColumnsFromDropdownData(dropdownData);
  }

  async applyFilter(field: string): Promise<void> {
    const filterType = this.getFilterType(field);

    if (filterType === 'multi-select') {
      if (this.multiFilters[field]?.length === 1) {
        this.currentFilters[field] = this.multiFilters[field][0];
      } else if (this.multiFilters[field]?.length > 1) {
        this.currentFilters[field] = this.multiFilters[field].join(',');
      } else {
        this.currentFilters[field] = null;
      }
    }

    this.showFloatingFilter = false;
    this.activeFilter = '';

    try {
      await this.refreshFilterOptionsFromCurrentSelection();
    } catch (error: any) {
      const errorMessage = error.message || 'Failed to refresh filter options';
      this.toastr.error(errorMessage, 'Error');
    }

    this.loadFirstPage();
  }

  applyDateRangeFilter(): void {
    this.showFloatingFilter = false;
    this.activeFilter = '';
    this.loadFirstPage();
  }

  clearDateRangeFilter(): void {
    this.dateRangeFilters.startDate = null;
    this.dateRangeFilters.endDate = null;
    this.showFloatingFilter = false;
    this.activeFilter = '';
    this.loadFirstPage();
  }

  async clearFilter(field: string): Promise<void> {
    const filterType = this.getFilterType(field);

    if (filterType === 'date-range') {
      this.clearDateRangeFilter();
      return;
    }

    this.multiFilters[field] = [];
    this.filterSearch[field] = '';
    this.currentFilters[field] = null;

    this.showFloatingFilter = false;
    this.activeFilter = '';

    this.loadFirstPage();
  }

  hasAnyFilterApplied(): boolean {
    const hasCurrentFilter = Object.values(this.currentFilters).some(value => value !== null);
    const hasDateFilter = this.dateRangeFilters.startDate !== null || this.dateRangeFilters.endDate !== null;
    return hasCurrentFilter || hasDateFilter;
  }

  async clearAllFilters(): Promise<void> {
    this.multiFilters = {};
    this.filterSearch = {};
    this.dateRangeFilters.startDate = null;
    this.dateRangeFilters.endDate = null;

    Object.keys(this.currentFilters).forEach(key => {
      this.currentFilters[key] = null;
    });

    this.activeFilter = '';
    this.showFloatingFilter = false;

    this.loadFirstPage();
  }

  async exportToCSV(): Promise<void> {
    if (!this.data.length) {
      this.toastr.info('No data available to export.');
      return;
    }

    const worksheetData = this.data.map((row: WaterAdminRow) => {
      const exportRow: Record<string, any> = {};

      this.columns.forEach((col) => {
        const value = row[col.field];
        exportRow[col.header] = Array.isArray(value) ? value.join(', ') : value ?? '';
      });

      return exportRow;
    });

    await exportRowsToExcel(
      worksheetData,
      this.columns.map((col) => col.header),
      'WaterAdminViewData.xlsx',
      'WaterAdminView'
    );
  }

  openExportDialog(): void {
    this.dialog.open(this.exportOptionsDialog, {
      width: '380px',
      disableClose: true,
    });
  }

  async downloadAllData(): Promise<void> {
    try {
      const { blob, fileName } = await this.waterAdminService.exportAllWaterAdminData();
      const url = URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      link.download = fileName;
      link.click();
      URL.revokeObjectURL(url);
      this.toastr.success('Export downloaded successfully.');
    } catch (error: any) {
      this.toastr.error(error?.message || 'Failed to download export.');
    }
  }

  isFilterActive(field: string): boolean {
    const filterType = this.getFilterType(field);
    
    if (filterType === 'multi-select') {
      return (this.multiFilters[field] || []).length > 0;
    } else if (filterType === 'date-range') {
      return this.dateRangeFilters.startDate !== null || this.dateRangeFilters.endDate !== null;
    }
    
    return false;
  }

  formatEffectiveDate(value: string | null | undefined): string {
    if (!value) {
      return '-';
    }

    const isoMatch = value.match(/^(\d{4})-(\d{2})-(\d{2})$/);
    if (isoMatch) {
      const [, year, month, day] = isoMatch;
      return `${month}/${day}/${year}`;
    }

    return value;
  }

  generateRowId(): string { 
    return `row_${Date.now()}_${Math.random().toString(36).substr(2,9)}`; 
  }
}
